'use client';

import { useState, useEffect } from 'react';
import { useReaderStore } from '@/stores/reader-store';
import { useLibraryStore } from '@/stores/library-store';
import { useReadingProgressStore } from '@/stores/reading-progress-store';
import { useRecentBooksStore } from '@/stores/recent-books-store';

/**
 * Returns true once all persisted Zustand stores have finished
 * rehydrating from IndexedDB.  With async storage the stores start
 * with defaults and asynchronously load the saved state — this hook
 * prevents the UI from rendering stale defaults.
 */
export function useHydrated(): boolean {
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const stores = [
      useReaderStore.persist,
      useLibraryStore.persist,
      useReadingProgressStore.persist,
      useRecentBooksStore.persist,
    ];

    // Check if all stores are already rehydrated
    if (stores.every((s) => s.hasHydrated())) {
      setHydrated(true);
      return;
    }

    // Otherwise wait for each store to finish rehydrating
    const unsubs = stores.map((s) =>
      s.onFinishHydration(() => {
        if (stores.every((st) => st.hasHydrated())) {
          setHydrated(true);
        }
      })
    );

    return () => unsubs.forEach((u) => u());
  }, []);

  return hydrated;
}
